import { StatsDataTable } from "../components/explore-statistics-table";
import { ExploreSchema, ModelTypes } from "../core/global";
import { ApiResponse, endpoint, err, getAxios, postAxios, processError, res } from "./axios";
import { onDemandSchemaResponse } from "./mock";

export const API_BASE = import.meta.env.VITE_API_BASE;

interface ModelProviderGeography {
  model: ModelTypes | null
  provider: string | null
  geography: string | null
  version?: string | null
}

interface BinaryProps extends ModelProviderGeography {
  source?: string       // 's3' | 'azure'
  key?: string
  lat?: number | string | null
  lon?: number | string | null
  lengthYears?: number | null
  dateEnd?: number | null
  dateStart?: number | null
  preview?: boolean
  orderId?: string | number | null
  cache?: boolean
  filename?: string
}

export const getBinaryData = async ({
  key,
  dateEnd,
  dateStart,
  lengthYears,
  orderId,
  version = null,
  ...props
}: BinaryProps):Promise<ApiResponse> => {
  try {
    
    const result = await postAxios(`/api/v1/data/`, {
      length_years: lengthYears,
      date_end: dateEnd,
      date_start: dateStart,
      order_id: orderId,
      key,
      version,
      ...props
    }, false, true)
    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, `Error getting binary data for **${key}**.`, true)
    return err(message, status)
  }
}
interface getUserCustomDataByLocationProps {
  lat?: number | string
  lon?: number | string
}

export const getUserCustomDataByLocation = async ({
  lat,
  lon,
}: getUserCustomDataByLocationProps):Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/data/get-user-custom-data-by-location`, {
      lat,
      lon,
    }, false)
    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, `Error getting custom user data.`, true)
    return err(message, status)
  }
}


interface UpdateUserCustomDataProps {
  id: number | string
  name?: string | null
}

export const updateUserCustomData = async ({
  id,
  name
}: UpdateUserCustomDataProps):Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/data/update-user-custom-data`, {
      id,
      name,
    }, false)
    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, `Error updating custom user data.`, true)
    return err(message, status)
  }
}

interface RoseProps extends ModelProviderGeography {
  magnitude: string | null, 
  direction: string | null,
  granularity?: number | null,
  time?: string | null,
  filename: string
  preview: boolean
  orderId?: string | number | null
}

interface RoseApiResponse extends ApiResponse {
  response: {
    plotData: {
      color: string
      label: string
      values: number[]
    }[]
    units: string
    radialAxisAngle: number
    frequencyIncrement: number
  } | null  
}

export const getRoseData = async ({
  orderId,
  ...props
}: RoseProps):Promise<RoseApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/data/get-rose`, {
      order_id: orderId,
      ...props
    }, true)

    const response = result?.data?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error getting rose data.", true)
    return err(message, status)
  }
}

interface DistributionProps extends ModelProviderGeography {
  vector_one: string | null, 
  vector_two: string | null,
  granularity?: number | null,
  time?: string | null,
  filename: string
  preview: boolean
  orderId?: string | number | null
}

interface DistributionApiResponse extends ApiResponse {
  response: {
      values: number[][],
      xAxis: number[],
      xAxisEdges: boolean,
      xDirectional: boolean,
      columnTotals: number[],
      yAxis: number[],
      yAxisEdges: boolean
      yDirectional: boolean
      rowTotals: number[],
    } | null  
}

export const getDistributionData = async ({
  orderId,
  ...props
}: DistributionProps):Promise<DistributionApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/data/get-distributions`, {
      order_id: orderId,
      ...props
    }, true)

    const response = result?.data?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error getting distributions data.", true)
    return err(message, status)
  }
}
interface GetStatsDataProps extends ModelProviderGeography {
  filename: string
  preview: boolean
  vector: string
  orderId?: string | number | null
}

interface StatsApiResponse extends ApiResponse {
  response: StatsDataTable | null  
}

export const getStatsData = async ({
  orderId,
  ...params
}: GetStatsDataProps):Promise<StatsApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/data/get-stats`, {
      order_id: orderId,
      ...params
    }, true)

    const response = result?.data?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error getting stats data.", true)
    return err(message, status)
  }
}

export interface HistogramData {
  ticks: number[]
  distribution: number[]
  units: string
}

interface GetHistogramDataProps extends ModelProviderGeography {
  filename: string
  preview: boolean
  vector: string
  orderId?: string | number | null
}

interface HistogramApiResponse extends ApiResponse {
  response: HistogramData | null  
}

export const getHistogramData = async ({
  orderId,
  ...props
}: GetHistogramDataProps):Promise<HistogramApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/data/get-histogram`, {
      order_id: orderId,
      ...props
    }, false)

    const response = result?.data?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error getting histogram data.", true)
    return err(message, status)
  }
}

interface SchemaApiResponse extends ApiResponse {
  response: ExploreSchema | null
}


export const getDataSchema = async ({
  source = 'azure',
  lengthYears,
  dateEnd,
  dateStart,
  orderId,
  version = null,
  ...props
}: BinaryProps):Promise<SchemaApiResponse> => {
  try {
    // to test ondemand data
    if (props?.model === "ondemand" ) {
      return res(onDemandSchemaResponse.schema)
    }

    const result = await postAxios(`/api/v1/data/get-schema`, {
      ...props,
      source,
      length_years: lengthYears,
      date_end: dateEnd,
      date_start: dateStart,
      order_id: orderId,
      version,
    }, false)

    const response = result?.data?.schema || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error getting binary data schema.", true)
    return err(message, status)
  }
}

export type GetDataDownloadLinkFormat = 'csv' | 'mat'

export interface GetDataDownloadLinkProps extends ModelProviderGeography {
  id: string | number
  orderId?: string | number | null
  filename: string | null
  format: GetDataDownloadLinkFormat
}

export const getDataDownloadLink = async ({
  id,
  orderId,
  ...props
}: GetDataDownloadLinkProps, isCustomData = false):Promise<ApiResponse> => {
  try {
    const result = await postAxios(`/api/v1/data/get-download-link/`, {
      order_items_id: id,
      order_id: orderId,
      is_custom_data: isCustomData,
      ...props
    }, false)

    const response = result?.data?.download || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error getting download link schema.", true)
    return err(message, status)
  }
}

export const loadAllVectors = async ():Promise<ApiResponse> => {
  try {
    
    const result = await postAxios(`/api/v1/data/get-all-vectors`, {}, false)

    const response = result?.data?.result || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error getting all vectors.", true)
    return err(message, status)
  }
}

export const getDataSources = async ():Promise<ApiResponse> => {
  try {
    
    const result = await postAxios(`/api/v1/data/get-data-sources`, {}, false)

    const response = result?.data?.result || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error getting data sources.", true)
    return err(message, status)
  }
}

interface UploadCustomDataProps {
  // This maps CSV header name to vector key
  mapping: {
    header: string, 
    vector: string,
    vector_id?: string | number | null
  }[]
  lat?: number | string
  lon?: number | string
  model?: ModelTypes
  provider?: string
  geography?: string
  file: File
  name?: string | null
  description?: string | null
}


export const uploadCustomData = async ({
  mapping,
  lat,
  lon,
  model,
  provider,
  geography,
  file,
  name,
  description,
}: UploadCustomDataProps):Promise<ApiResponse> => {
  try {
    const formData = new FormData();

    formData.append('file', file);
    formData.append('mapping', JSON.stringify(mapping));
    formData.append('lat', `${lat}`);
    formData.append('lon', `${lon}`);
    // TODO: remove model, provider and geography
    formData.append('model', model || '');
    formData.append('provider', provider || '');
    formData.append('geography', geography || '');
    formData.append('name', name || '');
    formData.append('description', description || '');

    const result = await endpoint.post(`${API_BASE}/api/v1/data/upload-custom-data`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })

    const response = result?.data?.result || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, "Error uploading data sources.", true)
    return err(message, status)
  }
}

interface BinaryProps extends ModelProviderGeography {
  lat_max?: number | null
  lat_min?: number | null
  lon_max?: number | null
  lon_min?: number | null
  layer?: string
}

export const getLayerXYZData = async ({
  layer,
  ...props
}: BinaryProps):Promise<ApiResponse> => {
  try {
    
    const result = await getAxios(`/api/v1/data/get-layer-xyz`, false, {
      layer,
      ...props
    })
    const response = result?.data || null
    return res(response)
  } catch (e) {
    const {status, message} = processError(e, `Error getting xyz values for **${layer}**.`, true)
    return err(message, status)
  }
}
